import { memo, useEffect, useState } from 'react';
import { AdjustableComponent } from '../../../../../utils/hooks/useClasses';
import { AxiosError } from 'axios';
import {
  Button,
  Icons,
  InputField,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Toggler,
  notify,
} from 'plume-ui';
import { ModalStyles } from 'plume-ui/dist/components/Modal/Modal';
import { TogglerElementTypes } from 'plume-ui/dist/components/Toggler/Toggler';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import { DependencyContainer } from '../../../../../DependencyContainer';
import { MixPanelEvents } from '../../../../../mixPanelEvents';
import { activePartnerAtom, webhookModalAtom } from '../../../../../state';
import FormattedMessage from '../../../../../utils/components/FormattedMessage';
import { useTrackEvent } from '../../../../trackingAnalytics/hooks/useTrackEvent';
import { defaultWebhookCredentials } from '../fixtures';
import {
  AuthType,
  TestingWebhook,
  WebhookAuthType,
  WebhookCredentials,
  Webhook as WebhookType,
} from '../types';
import { ErrorResponse } from '../../../../../types';
import { useForm } from 'plume-web-utils';

type WebhookModalProps = {};
type WebhookModalStyles = {};

const { configurationService } = new DependencyContainer();

const WebhookModal: AdjustableComponent<
  WebhookModalProps,
  WebhookModalStyles
> = () => {
  const { t } = useTranslation();
  const trackEvent = useTrackEvent();
  const modalProps = useRecoilValue(webhookModalAtom);
  const resetModal = useResetRecoilState(webhookModalAtom);
  const activePartner = useRecoilValue(activePartnerAtom);
  const [saving, setSaving] = useState(false);
  const [testState, setTestState] = useState<
    'error' | 'success' | 'ongoing' | undefined
  >(undefined);

  const form = useForm<WebhookType>({
    initialValues: {
      name: modalProps.webhook.name || '',
      urlEndpoint: modalProps.webhook.urlEndpoint || '',
      authType: modalProps.webhook.authType,
      enabled: true,
      credentials:
        modalProps.webhook.credentials ||
        (modalProps.webhook.authType === 'basicAuth'
          ? defaultWebhookCredentials
          : ''),
    },
    validationSchema: {
      name: { required: true },
      urlEndpoint: { required: true },
      credentials: {
        required: true,
        validator: (value: WebhookCredentials) => {
          if (typeof value === 'string') {
            return value.length > 0;
          }
          return Boolean(
            value.username &&
              value.password &&
              value.username.length > 0 &&
              value.password.length > 0,
          );
        },
      },
    },
    validateOnChange: true,
  });

  const credentials: WebhookAuthType[] = [
    {
      key: 'basicAuth',
      label: t('configuration.modals.webhook.creds.basic'),
    },
    {
      key: 'apiKey',
      label: t('configuration.modals.webhook.creds.api'),
    },
  ];

  const handleAuthTypeToggle = (item: TogglerElementTypes) => {
    const newAuthType = item.key as AuthType;
    form.setValues({
      ...form.values,
      authType: newAuthType,
      credentials: newAuthType === 'basicAuth' ? defaultWebhookCredentials : '',
    });
  };

  const handleFinishError = (message?: string) => {
    notify({
      title: t(
        `configuration.notifications.error.webhook.${modalProps.type}.title`,
      ),
      body:
        message ||
        t(`configuration.notifications.error.webhook.${modalProps.type}.body`),
      type: 'error',
    });
    trackEvent({
      eventName: MixPanelEvents.WEBHOOK_FAILURE,
      additionalContent: {
        INTERACTION: modalProps.type.toUpperCase(),
        WEBHOOK_ID:
          modalProps.type === 'edit' ? modalProps.webhook.id : undefined,
        ERROR_MESSAGE: message || 'no message',
      },
    });
    setSaving(false);
  };

  const parseWebhookObjectForUpdate = (
    data: WebhookType,
  ): Partial<WebhookType> => ({
    name: data.name,
    authType: data.authType,
    urlEndpoint: data.urlEndpoint,
    credentials: data.credentials,
    enabled: true,
  });

  const handleFinishClick = async () => {
    if (saving || !form.validateForm()) {
      return;
    }

    setSaving(true);

    if (!activePartner?.id) {
      setSaving(false);
      return;
    }

    try {
      if (modalProps.type === 'edit' && modalProps.webhook.id) {
        await configurationService.updateWebhook(
          activePartner.id,
          modalProps.webhook.id,
          parseWebhookObjectForUpdate(form.values),
        );
      } else {
        await configurationService.addWebhook(
          activePartner.id,
          form.values as WebhookType,
        );
      }

      notify({
        title: t(
          `configuration.notifications.success.webhook.${modalProps.type}.title`,
        ),
        body: t(
          `configuration.notifications.success.webhook.${modalProps.type}.body`,
        ),
        type: 'success',
      });

      trackEvent({
        eventName: MixPanelEvents.WEBHOOK_SUCCESS,
        additionalContent: {
          INTERACTION: modalProps.type.toUpperCase(),
          WEBHOOK_ID:
            modalProps.type === 'edit' ? modalProps.webhook.id : undefined,
        },
      });

      modalProps.refetchWebhooks();
      setTestState(undefined);
      setSaving(false);
      resetModal();
    } catch (error) {
      handleFinishError(
        (error as AxiosError<ErrorResponse>).response?.data?.message,
      );
    }
  };

  const handleTestWebhook = async () => {
    setTestState('ongoing');
    if (!form.isValid) {
      setTestState('error');
      return;
    }

    const testData: TestingWebhook = {
      authType: form.values.authType,
      urlEndpoint: form.values.urlEndpoint,
      credentials: form.values.credentials,
    };

    const res = await modalProps.testWebhook(testData);
    setTestState(res?.status === 'verified' ? 'success' : 'error');
  };

  useEffect(() => {
    form.setValues({
      name: modalProps.webhook.name || '',
      urlEndpoint: modalProps.webhook.urlEndpoint || '',
      authType: modalProps.webhook.authType,
      enabled: true,
      credentials:
        modalProps.webhook.credentials ||
        (modalProps.webhook.authType === 'basicAuth'
          ? defaultWebhookCredentials
          : ''),
    });
  }, [modalProps.isOpen]);

  const isCredentialsObject = (
    credentials: WebhookCredentials,
  ): credentials is { username: string; password: string } => {
    return (
      typeof credentials === 'object' &&
      'username' in credentials &&
      'password' in credentials
    );
  };

  return (
    <Modal
      classes={(current: ModalStyles) => ({
        root: `${current.root} Configuration__modal Configuration__modal--webhook`,
      })}
      isOpen={modalProps.isOpen}
      onRequestClose={() => {
        resetModal();
        setTestState(undefined);
      }}
      ariaHideApp={false}
    >
      <ModalHeader
        title={
          modalProps.type === 'add'
            ? t('configuration.modals.webhook.titleNew')
            : t('configuration.modals.webhook.titleEdit')
        }
      />
      <ModalBody>
        <div className="Configuration__modal__inputs-container">
          <InputField
            classes={(current) => ({
              ...current,
              root: `${current.root} ${
                form.values.name === '' ? 'InputField--empty' : ''
              }`,
            })}
            id="input-name"
            label={t('configuration.modals.webhook.name')}
            required
            requiredAsterix
            value={form.values.name}
            onChange={(event) => form.handleChange('name', event.target.value)}
            onBlur={() => form.handleBlur('name')}
            messages={
              form.touched.name && form.errors.name
                ? [
                    {
                      status: 'error',
                      message: form.errors.name,
                    },
                  ]
                : undefined
            }
          />
          <InputField
            classes={(current) => ({
              ...current,
              root: `${current.root} ${
                form.values.urlEndpoint === '' ? 'InputField--empty' : ''
              }`,
            })}
            id="input-platformUrl"
            label={t('configuration.modals.webhook.platformUrl')}
            required
            requiredAsterix
            value={form.values.urlEndpoint}
            onChange={(event) =>
              form.handleChange('urlEndpoint', event.target.value)
            }
            onBlur={() => form.handleBlur('urlEndpoint')}
            messages={
              form.touched.urlEndpoint && form.errors.urlEndpoint
                ? [
                    {
                      status: 'error',
                      message: form.errors.urlEndpoint,
                    },
                  ]
                : undefined
            }
          />
          <div className="Configuration__modal__inputs-container__toggler">
            <span className="InputField__label--focus InputField__label--required">
              <FormattedMessage id="configuration.modals.webhook.credentials" />
            </span>
            <Toggler
              toggleElements={credentials}
              onToggle={handleAuthTypeToggle}
              value={form.values.authType}
            />
          </div>
          {form.values.authType === 'basicAuth' &&
            isCredentialsObject(form.values.credentials) && (
              <div className="Configuration__modal__inputs-container__credentials">
                <InputField
                  classes={(current) => ({
                    ...current,
                    root: `${current.root} ${
                      isCredentialsObject(form.values.credentials) &&
                      form.values.credentials.username?.length > 0
                        ? ''
                        : 'InputField--empty'
                    }`,
                  })}
                  id="input-username"
                  label={t('configuration.modals.webhook.username')}
                  type="password"
                  required
                  requiredAsterix
                  value={form.values.credentials.username}
                  onChange={(event) =>
                    form.handleChange('credentials', {
                      ...(isCredentialsObject(form.values.credentials)
                        ? form.values.credentials
                        : { password: '' }),
                      username: event.target.value,
                    })
                  }
                  onBlur={() => form.handleBlur('credentials')}
                />
                <InputField
                  classes={(current) => ({
                    ...current,
                    root: `${current.root} ${
                      isCredentialsObject(form.values.credentials) &&
                      form.values.credentials.password?.length > 0
                        ? ''
                        : 'InputField--empty'
                    }`,
                  })}
                  id="input-password"
                  label={t('configuration.modals.webhook.password')}
                  type="password"
                  required
                  requiredAsterix
                  value={form.values.credentials.password}
                  onChange={(event) =>
                    form.handleChange('credentials', {
                      ...(isCredentialsObject(form.values.credentials)
                        ? form.values.credentials
                        : { username: '', password: '' }),
                      password: event.target.value || '',
                    })
                  }
                  onBlur={() => form.handleBlur('credentials')}
                />
              </div>
            )}
          {form.values.authType === 'apiKey' &&
            typeof form.values.credentials === 'string' && (
              <InputField
                classes={(current) => ({
                  ...current,
                  root: `${current.root} ${
                    form.values.credentials === '' ? 'InputField--empty' : ''
                  }`,
                })}
                id="input-apiKey"
                label={t('configuration.modals.webhook.apiKey')}
                type="password"
                required
                requiredAsterix
                value={form.values.credentials}
                onChange={(event) =>
                  form.handleChange('credentials', event.target.value)
                }
                onBlur={() => form.handleBlur('credentials')}
              />
            )}
        </div>
        <div className="Configuration__modal__test">
          <Button styleVariant="tertiary" onClick={handleTestWebhook}>
            <FormattedMessage id="configuration.modals.webhook.test" />
          </Button>
          {testState === 'ongoing' && <Spinner visible />}
          {testState === 'success' && <Icons.CheckedIcon width={24} />}
          {testState === 'error' && (
            <div className="Configuration__modal__test__error-message">
              {t('configuration.modals.webhook.testError')}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          styleVariant="tertiary-grey"
          onClick={() => {
            resetModal();
            setTestState(undefined);
          }}
        >
          <FormattedMessage id="confirmation.cancel" />
        </Button>
        <Button
          styleVariant="superprimary"
          onClick={handleFinishClick}
          disabled={saving || !form.isValid}
        >
          <FormattedMessage id="confirmation.finish" />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default memo(WebhookModal);
